import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Typography,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axiosInstance from '../../../utils/axios';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import FeedbackMessage from '../../common/FeedbackMessage';
import { SUCCESS } from '../../common/SaaSTokens';
import ListFilterBar from '../../common/ListFilterBar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const NEON = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const DOT_GRID = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;
const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

const MotionCard = motion(Card);

const cardShellSx = {
  borderRadius: '14px',
  background: 'linear-gradient(160deg, rgba(5,13,26,0.97) 0%, rgba(10,22,40,0.95) 100%)',
  border: '1px solid rgba(56,189,248,0.17)',
  boxShadow: [
    '0 0 0 1px rgba(30,58,138,0.9)',
    '0 4px 24px rgba(0,0,0,0.45)',
    '0 20px 60px rgba(0,0,0,0.3)',
    'inset 0 1px 0 rgba(56,189,248,0.1)',
  ].join(', '),
  backdropFilter: 'blur(20px)',
  overflow: 'hidden',
  position: 'relative',
};

const BudgetExpenses = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [chartMetricFilter, setChartMetricFilter] = useState('all');

  const filterOptions = useMemo(() => ([
    { name: 'date_from', label: 'Start date', type: 'date' },
    { name: 'date_to', label: 'End date', type: 'date' },
  ]), []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (!filters.date_from && !filters.date_to) {
        params.year = new Date().getFullYear();
      }
      const response = await axiosInstance.get('/budget-expenses', { params });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch budget expenses data');
      console.error('Error fetching budget expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_from, filters.date_to]);

  const isReportMenuOpen = Boolean(reportMenuAnchor);

  const closeReportMenu = () => setReportMenuAnchor(null);

  const openReportMenu = (event) => {
    setReportMenuAnchor(event.currentTarget);
  };

  const buildMonthlyReportRows = () => (
    chartRows.map((month, index) => ({
      no: index + 1,
      month: month.month_name || '-',
      budget: Number(month.budget || 0),
      claimable: Number(month.claimable || 0),
      unclaimable: Number(month.unclaimable || 0),
      remaining: Number(month.remaining || (month.budget || 0) - (month.claimable || 0) - (month.unclaimable || 0)),
    }))
  );

  const generateCsvReport = () => {
    const rows = buildMonthlyReportRows();
    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm');
    const quoteCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const headers = ['No', 'Month', 'Budget (RM)', 'Claimable (RM)', 'Unclaimable (RM)', 'Remaining (RM)'];

    const summaryRows = [
      ['BUDGET EXPENSES REPORT'],
      ['Generated At', generatedAt],
      ['Start Date', filters.date_from || '—'],
      ['End Date', filters.date_to || '—'],
      ['Search', searchTerm || 'None'],
      ['Total Records', rows.length],
      [''],
    ];

    const csvRows = rows.map((row) => [
      row.no,
      quoteCsv(row.month),
      row.budget.toFixed(2),
      row.claimable.toFixed(2),
      row.unclaimable.toFixed(2),
      row.remaining.toFixed(2),
    ]);

    const csvContent = [...summaryRows, headers, ...csvRows]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Budget_Expenses_Report_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const generatePdfReport = () => {
    const rows = buildMonthlyReportRows();
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const marginX = 36;
    const pageWidth = doc.internal.pageSize.getWidth();
    const baseTable = {
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 5,
        overflow: 'linebreak',
        textColor: [30, 30, 30],
      },
      headStyles: {
        fillColor: [20, 55, 90],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      margin: { left: marginX, right: marginX },
      tableWidth: pageWidth - marginX * 2,
      theme: 'striped',
      alternateRowStyles: { fillColor: [248, 250, 252] },
    };

    let y = 38;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Budget Expenses Report', pageWidth / 2, y, { align: 'center' });
    y += 22;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, marginX, y);
    y += 14;
    doc.text(`Start date: ${filters.date_from || '—'}`, marginX, y);
    y += 14;
    doc.text(`End date: ${filters.date_to || '—'}`, marginX, y);
    y += 14;
    doc.text(`Search: ${searchTerm || 'None'}`, marginX, y);
    y += 14;
    doc.text(`Records: ${rows.length}`, marginX, y);
    y += 18;

    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [['No', 'Month', 'Budget (RM)', 'Claimable (RM)', 'Unclaimable (RM)', 'Remaining (RM)']],
      body: rows.map((row) => [
        row.no,
        row.month,
        row.budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        row.claimable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        row.unclaimable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        row.remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      ]),
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${currentPage} of ${pageCount}`,
          pageWidth - marginX,
          doc.internal.pageSize.getHeight() - 16,
          { align: 'right' }
        );
      },
    });

    doc.save(`Budget_Expenses_Report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
  };

  const handleGenerateReport = async (type) => {
    try {
      setReportLoading(true);
      closeReportMenu();
      if (type === 'csv') {
        generateCsvReport();
      } else {
        generatePdfReport();
      }
      setSuccessMessage(`Budget expenses report (${type.toUpperCase()}) generated successfully.`);
    } catch (err) {
      setError('Failed to generate report');
      console.error('Error generating report:', err);
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Loading message="Loading budget expenses data" size={56} variant="primary" />
      </Box>
    );
  }

  const chartRows = (data?.monthly_data || []).filter((month) =>
    month.month_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStatCardClick = (_, index) => {
    const filterMap = ['budget', 'claimable', 'unclaimable', 'remaining'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = chartMetricFilter === selectedFilter ? 'all' : selectedFilter;
    setChartMetricFilter(nextFilter);
  };

  const activeStatIndex =
    chartMetricFilter === 'budget' ? 0 :
    chartMetricFilter === 'claimable' ? 1 :
    chartMetricFilter === 'unclaimable' ? 2 :
    chartMetricFilter === 'remaining' ? 3 :
    null;

  const statsData = data ? [
    {
      icon: BarChartIcon,
      value: `RM ${Number(data.yearly_summary?.total_budget || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      label: 'Total Budget',
      color: 'primary',
      valueSize: 'small',
    },
    {
      icon: TrendingUpIcon,
      value: `RM ${Number(data.yearly_summary?.total_claimable || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      label: 'Claimable',
      color: 'success',
      valueSize: 'small',
    },
    {
      icon: ReportIcon,
      value: `RM ${Number(data.yearly_summary?.total_unclaimable || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      label: 'Unclaimable',
      color: 'warning',
      valueSize: 'small',
    },
    {
      icon: BarChartIcon,
      value: `RM ${Number(data.yearly_summary?.budget_remaining || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      label: 'Remaining',
      color: 'info',
      valueSize: 'small',
    },
  ] : [];

  const chartDatasets = [
    {
      key: 'budget',
      label: 'Budget',
      data: chartRows.map((month) => month.budget),
      backgroundColor: 'rgba(56,189,248,0.7)',
      borderColor: NEON,
      borderWidth: 1.5,
      borderRadius: 6,
    },
    {
      key: 'claimable',
      label: 'Claimable',
      data: chartRows.map((month) => month.claimable),
      backgroundColor: 'rgba(52,211,153,0.7)',
      borderColor: NEON3,
      borderWidth: 1.5,
      borderRadius: 6,
    },
    {
      key: 'unclaimable',
      label: 'Unclaimable',
      data: chartRows.map((month) => month.unclaimable),
      backgroundColor: 'rgba(251,146,60,0.7)',
      borderColor: '#FB923C',
      borderWidth: 1.5,
      borderRadius: 6,
    },
    {
      key: 'remaining',
      label: 'Remaining',
      data: chartRows.map((month) => {
        const budget = Number(month.budget || 0);
        const spent = Number(month.claimable || 0) + Number(month.unclaimable || 0);
        return budget - spent;
      }),
      backgroundColor: 'rgba(129,140,248,0.7)',
      borderColor: NEON2,
      borderWidth: 1.5,
      borderRadius: 6,
    },
  ];

  const visibleChartDatasets = chartMetricFilter === 'all'
    ? chartDatasets.filter((ds) => ds.key !== 'remaining')
    : chartDatasets.filter((ds) => ds.key === chartMetricFilter);

  // Budget totals are much larger than individual claim amounts — use a right axis for claims.
  const usesDualAxis = chartMetricFilter === 'all'
    && visibleChartDatasets.some((ds) => ds.key === 'budget')
    && visibleChartDatasets.some((ds) => ds.key === 'claimable' || ds.key === 'unclaimable');

  const chartData = {
    labels: chartRows.map((month) => month.month_name),
    datasets: visibleChartDatasets.map(({ key, ...dataset }) => ({
      ...dataset,
      yAxisID: usesDualAxis && key !== 'budget' ? 'y1' : 'y',
      maxBarThickness: usesDualAxis && key !== 'budget' ? 28 : 48,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          fontFamily: "'DM Sans', sans-serif",
          color: 'rgba(226,232,240,0.7)',
          usePointStyle: true,
          padding: 20,
          font: { size: 12, weight: '500' },
        },
      },
      title: {
        display: true,
        text: `Budget vs Expenses${filters.date_from || filters.date_to ? ` (${filters.date_from || '…'} – ${filters.date_to || '…'})` : ` (${new Date().getFullYear()})`}`,
        color: 'rgba(226,232,240,0.9)',
        font: { size: 14, weight: '600', family: "'Rajdhani', sans-serif" },
        padding: 20,
      },
      tooltip: {
        backgroundColor: 'rgba(5,13,26,0.95)',
        borderColor: 'rgba(56,189,248,0.3)',
        borderWidth: 1,
        titleColor: 'rgba(226,232,240,0.9)',
        bodyColor: 'rgba(226,232,240,0.8)',
        bodyFont: { family: "'DM Sans', sans-serif", size: 11 },
        padding: 12,
        displayColors: true,
        callbacks: {
          label: (context) => `${context.dataset.label}: RM ${context.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
        title: usesDualAxis ? {
          display: true,
          text: 'Budget (RM)',
          color: 'rgba(56,189,248,0.75)',
          font: { family: "'DM Sans', sans-serif", size: 11, weight: 600 },
        } : undefined,
        grid: { color: 'rgba(56,189,248,0.08)', drawBorder: false },
        ticks: {
          color: 'rgba(56,189,248,0.5)',
          font: { family: "'JetBrains Mono', monospace", size: 11 },
          callback: (value) => `RM ${Number(value).toLocaleString()}`,
        },
      },
      ...(usesDualAxis ? {
        y1: {
          beginAtZero: true,
          position: 'right',
          title: {
            display: true,
            text: 'Claims (RM)',
            color: 'rgba(52,211,153,0.85)',
            font: { family: "'DM Sans', sans-serif", size: 11, weight: 600 },
          },
          grid: { drawOnChartArea: false, drawBorder: false },
          ticks: {
            color: 'rgba(52,211,153,0.65)',
            font: { family: "'JetBrains Mono', monospace", size: 11 },
            callback: (value) => `RM ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
          },
        },
      } : {}),
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: 'rgba(56,189,248,0.5)',
          font: { family: "'DM Sans', sans-serif", size: 11 },
        },
      },
    },
    interaction: { intersect: false, mode: 'index' },
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Budget Expenses"
        subtitle="View budget vs actual expenses with detailed reporting and analysis"
        icon={BarChartIcon}
        actionButton={(
          <Button
            variant="contained"
            startIcon={reportLoading ? <CircularProgress size={18} sx={{ color: NEON }} /> : <ReportIcon />}
            onClick={openReportMenu}
            disabled={reportLoading || chartRows.length === 0}
            sx={{
              borderRadius: '10px',
              px: 3,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: '0.875rem',
              letterSpacing: 0.3,
              background: 'linear-gradient(135deg, rgba(56,189,248,0.22), rgba(37,99,235,0.55))',
              border: '1px solid rgba(56,189,248,0.4)',
              color: NEON,
              boxShadow: '0 0 18px rgba(56,189,248,0.3), 0 4px 14px rgba(0,0,0,0.4)',
              transition: 'all 0.22s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(56,189,248,0.32), rgba(37,99,235,0.65))',
                boxShadow: '0 0 28px rgba(56,189,248,0.5), 0 6px 20px rgba(0,0,0,0.45)',
                border: '1px solid rgba(56,189,248,0.65)',
                color: '#fff',
              },
              '&.Mui-disabled': {
                background: 'rgba(15,31,56,0.7)',
                color: 'rgba(56,189,248,0.25)',
                boxShadow: 'none',
                border: '1px solid rgba(56,189,248,0.1)',
              },
            }}
          >
            {reportLoading ? 'Generating...' : 'Generate Report'}
          </Button>
        )}
      />

      <Menu
        anchorEl={reportMenuAnchor}
        open={isReportMenuOpen}
        onClose={closeReportMenu}
        PaperProps={{
          elevation: 6,
          sx: {
            mt: 1,
            minWidth: 210,
            borderRadius: 2,
            border: `1px solid ${alpha('#38BDF8', 0.28)}`,
            background: `linear-gradient(155deg, ${alpha('#050d1a', 0.98)}, ${alpha('#0a1628', 0.95)})`,
            backdropFilter: 'blur(8px)',
            boxShadow: `0 8px 32px ${alpha('#000', 0.55)}`,
            '& .MuiMenuItem-root': {
              color: alpha('#E2E8F0', 0.9),
              borderRadius: 1.25,
              mx: 0.5,
              my: 0.25,
              '& .MuiListItemIcon-root': {
                color: alpha('#38BDF8', 0.88),
                minWidth: 32,
              },
              '& .MuiListItemText-secondary': {
                color: alpha('#94A3B8', 0.88),
              },
              '&:hover': {
                backgroundColor: alpha('#38BDF8', 0.12),
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => handleGenerateReport('csv')} disabled={reportLoading}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download CSV Report"
            secondary="Spreadsheet-compatible format"
          />
        </MenuItem>
        <MenuItem onClick={() => handleGenerateReport('pdf')} disabled={reportLoading}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download PDF Report"
            secondary="Printable report format"
          />
        </MenuItem>
      </Menu>

      <FeedbackMessage
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage('')}
        sx={{
          mb: 2.5,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
        }}
      />
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          mb: 2.5,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.9rem',
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          color: '#fca5a5',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
        }}
      />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters((prev) => !prev)}
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={() => {
          setFilters({});
          setSearchTerm('');
          setChartMetricFilter('all');
        }}
        filterOptions={filterOptions}
        searchPlaceholder="Search month name..."
      />

      {data ? (
        <>
          <StatCards
            stats={statsData}
            compact
            onCardClick={handleStatCardClick}
            activeIndex={activeStatIndex}
          />

          <MotionCard
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
            elevation={0}
            sx={cardShellSx}
          >
            <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
            <Box sx={{ position: 'absolute', inset: 0, background: SCAN_LINES, pointerEvents: 'none', zIndex: 0, opacity: 0.65 }} />
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              {chartRows.length > 0 ? (
                <Box sx={{ height: 420 }}>
                  <Bar data={chartData} options={chartOptions} />
                </Box>
              ) : (
                <Box sx={{ minHeight: 280, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
                  <Box>
                    <BarChartIcon sx={{ fontSize: 44, color: 'rgba(56,189,248,0.45)', mb: 1 }} />
                    <Typography sx={{ color: 'rgba(226,232,240,0.88)', fontWeight: 600 }}>
                      No chart data for this filter
                    </Typography>
                    <Typography sx={{ color: 'rgba(148,163,184,0.9)', fontSize: '0.9rem', mt: 0.5 }}>
                      Try another month, clear filters, or remove search text.
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </>
      ) : null}
    </Container>
  );
};

export default BudgetExpenses;
